import Axios from "../../setting/axios";
import { useMutation, useQuery } from "react-query";

// PAGE VIEWS FUNCTIONS
const AddPageView = async (projectId) => {
  console.log("=== [AddPageView Function] START ===");
  console.log("[AddPageView] Function called with project ID:", projectId);

  try {
    console.log("[AddPageView] Step 1: Fetching client IP from ipify API...");

    // Fetch client IP from ipify API
    const ipResponse = await fetch("https://api.ipify.org?format=json");
    console.log("[AddPageView] IP API response status:", ipResponse.status);

    if (!ipResponse.ok) {
      throw new Error(`IP API failed with status: ${ipResponse.status}`);
    }

    const ipData = await ipResponse.json();
    const { ip } = ipData;
    console.log("[AddPageView] Client IP fetched successfully:", ip);
    console.log("[AddPageView] Full IP response:", ipData);

    console.log("[AddPageView] Step 2: Preparing Axios request payload...");
    const payload = { prj_id: projectId, client_ip: ip };
    console.log("[AddPageView] Payload to send:", payload);

    console.log(
      "[AddPageView] Step 3: Making Axios POST request to /page-views/add-page-view..."
    );
    console.log("[AddPageView] Axios instance config:", Axios.defaults);

    const response = await Axios.post("/page-views/add-page-view", payload, {
      headers: { "Content-Type": "application/json" },
      timeout: 10000,
    });

    console.log("[AddPageView] Axios response received:");
    console.log("[AddPageView] Response status:", response.status);
    console.log("[AddPageView] Response status text:", response.statusText);
    console.log("[AddPageView] Response data:", response.data);
    console.log("[AddPageView] Response headers:", response.headers);

    if (response.data && response.data.success !== false) {
      console.log("[AddPageView] API call successful - returning data");
      console.log("=== [AddPageView Function] END - SUCCESS ===");
      return response.data;
    } else {
      console.error("[AddPageView] API returned error in data:", response.data);
      throw new Error(response.data?.error || "Failed to track page view");
    }
  } catch (err) {
    console.error("[AddPageView] ERROR OCCURRED:");
    console.error("[AddPageView] Error message:", err.message);
    console.error("[AddPageView] Full error object:", err);
    console.error(
      "[AddPageView] Error response (if axios):",
      err.response?.data
    );
    console.error(
      "[AddPageView] Error status (if axios):",
      err.response?.status
    );
    console.log("=== [AddPageView Function] END - ERROR ===");
    throw new Error(
      err.response?.data?.error || err.message || "Failed to track page view"
    );
  }
};

const GetProjectViews = async (projectId) => {
  console.log("=== [GetProjectViews Function] START ===");
  console.log("[GetProjectViews] Function called with project ID:", projectId);

  try {
    console.log("[GetProjectViews] Preparing Axios request payload...");
    const payload = { prj_id: projectId };
    console.log("[GetProjectViews] Payload to send:", payload);

    console.log(
      "[GetProjectViews] Making Axios POST request to /page-views/get-project-views..."
    );
    console.log("[GetProjectViews] Axios instance config:", Axios.defaults);

    const response = await Axios.post(
      "/page-views/get-project-views",
      payload,
      {
        headers: { "Content-Type": "application/json" },
        timeout: 10000,
      }
    );

    console.log("[GetProjectViews] Axios response received:");
    console.log("[GetProjectViews] Response status:", response.status);
    console.log("[GetProjectViews] Response status text:", response.statusText);
    console.log("[GetProjectViews] Response data:", response.data);
    console.log("[GetProjectViews] Response headers:", response.headers);

    if (response.data && response.data.success !== false) {
      console.log("[GetProjectViews] API call successful - returning data");
      console.log("=== [GetProjectViews Function] END - SUCCESS ===");
      return response.data;
    } else {
      console.error(
        "[GetProjectViews] API returned error in data:",
        response.data
      );
      throw new Error(response.data?.error || "Failed to get project views");
    }
  } catch (err) {
    console.error("[GetProjectViews] ERROR OCCURRED:");
    console.error("[GetProjectViews] Error message:", err.message);
    console.error("[GetProjectViews] Full error object:", err);
    console.error(
      "[GetProjectViews] Error response (if axios):",
      err.response?.data
    );
    console.error(
      "[GetProjectViews] Error status (if axios):",
      err.response?.status
    );
    console.log("=== [GetProjectViews Function] END - ERROR ===");
    throw new Error(
      err.response?.data?.error || err.message || "Failed to get project views"
    );
  }
};

// PAGE VIEWS HOOKS
export const useAddPageView = () => {
  console.log("=== [useAddPageView Hook] INITIALIZATION ===");
  console.log("[useAddPageView] Hook initialized");

  const mutation = useMutation(AddPageView, {
    retry: false,
    refetchOnWindowFocus: false,
    onMutate: (variables) => {
      console.log(
        "[useAddPageView] Mutation starting with variables:",
        variables
      );
      // Cancel any outgoing refetches
      // return ctx;
    },
    onSuccess: (data, variables, context) => {
      console.log("=== [useAddPageView] ON SUCCESS ===");
      console.log("[useAddPageView] Mutation success - variables:", variables);
      console.log("[useAddPageView] Mutation success - data:", data);
      console.log("[useAddPageView] Mutation success - context:", context);

      if (!data.skipped) {
        console.log("[useAddPageView] Page view tracked successfully");
      } else {
        console.log(
          `[useAddPageView] View skipped - cooldown remaining: ${data.cooldownRemaining} minutes`
        );
      }
      console.log("=== [useAddPageView] ON SUCCESS END ===");
    },
    onError: (error, variables, context) => {
      console.log("=== [useAddPageView] ON ERROR ===");
      console.error("[useAddPageView] Mutation error - variables:", variables);
      console.error("[useAddPageView] Mutation error - error:", error);
      console.error("[useAddPageView] Mutation error - context:", context);
      console.error("[useAddPageView] Mutation error message:", error.message);
      console.log("=== [useAddPageView] ON ERROR END ===");
    },
    onSettled: (data, error) => {
      console.log(
        "[useAddPageView] Mutation settled - data:",
        !!data,
        "error:",
        !!error
      );
    },
  });

  console.log("[useAddPageView] Mutation object created:", {
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    data: mutation.data,
    error: mutation.error,
  });

  console.log("=== [useAddPageView Hook] INITIALIZATION END ===");
  return mutation;
};

export const useGetProjectViews = (projectId) => {
  console.log("=== [useGetProjectViews Hook] INITIALIZATION ===");
  console.log(
    "[useGetProjectViews] Hook initialized with project ID:",
    projectId
  );

  const query = useQuery(
    ["project-views", projectId],
    () => GetProjectViews(projectId),
    {
      enabled: !!projectId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onSuccess: (data) => {
        console.log("=== [useGetProjectViews] ON SUCCESS ===");
        console.log("[useGetProjectViews] Query success - data:", data);
        console.log("=== [useGetProjectViews] ON SUCCESS END ===");
      },
      onError: (error) => {
        console.log("=== [useGetProjectViews] ON ERROR ===");
        console.error("[useGetProjectViews] Query error:", error);
        console.error(
          "[useGetProjectViews] Query error message:",
          error.message
        );
        console.log("=== [useGetProjectViews] ON ERROR END ===");
      },
      onSettled: (data, error) => {
        console.log(
          "[useGetProjectViews] Query settled - data:",
          !!data,
          "error:",
          !!error
        );
      },
    }
  );

  console.log("[useGetProjectViews] Query object created:", {
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    data: !!query.data,
    error: query.error,
  });

  console.log("=== [useGetProjectViews Hook] INITIALIZATION END ===");
  return query;
};
